import {clsx} from 'clsx'
import {Tooltip, useOnEscapePress} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {memo, use, useRef, useState} from 'react'
import type {AnchorHTMLAttributes, ComponentProps, FocusEvent, MouseEvent, ReactNode, Ref} from 'react'
import {LayoutLink as Link} from '../layout-runtime'
import {useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import {GroupedContext} from './sidebar-context'
import {getCurrentState, sidebarChildKind} from './contains-current'
import {SidebarAction} from './Action'
import actionStyles from './Action.module.css'
import styles from './Item.module.css'
import rowStyles from './Row.module.css'

type SidebarItemLinkProps =
  | {
      /** Destination for a native link. */
      href: string
      to?: never
      forceHardNavigation?: never
    }
  | {
      /** Destination for client-side navigation through the host router. */
      to: string
      href?: never
      /** Renders `to` as a native link that triggers a full page load. */
      forceHardNavigation?: boolean
    }

export type ItemProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> &
  SidebarItemLinkProps & {
    /** Text or non-interactive inline content naming the destination. */
    children: ReactNode
    /** Router preload behavior, used with `to`. */
    preload?: ComponentProps<typeof Link>['preload']
    /** Router state passed with `to`. */
    state?: ComponentProps<typeof Link>['state']
    /**
     * Non-interactive visual before the label, such as `<Icon aria-hidden />` or an avatar.
     * Required for top-level items so they stay visible in the collapsed rail.
     */
    leadingVisual?: ReactNode
    /** Non-interactive metadata after the label, such as a counter. Hidden in the collapsed rail. */
    trailingVisual?: ReactNode
    /** A separately focusable control rendered next to the link, such as a menu trigger. */
    trailingAction?: ReactNode
    trailingActionLayout?: 'adjacent' | 'overlay'
    /** `hover` reveals the action on hover or focus; `muted` keeps it visible at reduced opacity. */
    actionVisibility?: 'always' | 'hover' | 'muted'
    /** Rail tooltip text for items whose label is not a plain string. */
    tooltipText?: string
    /** Renders the item as an unavailable button and shows this explanation in its tooltip. */
    inactiveText?: string
    /** Marks the item as the current page. Defaults `aria-current` to `page`. */
    current?: boolean
    /** Ref to the link, or to the button when `inactiveText` is set. */
    ref?: Ref<HTMLAnchorElement | HTMLButtonElement>
  }

type ConsumerHandlers = Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'onFocus' | 'onMouseEnter' | 'onMouseOverCapture'>

// Keep consumer handlers out of Tooltip's cloning and delayed event forwarding.
function ItemLink({
  children,
  consumerHandlers,
  expandedDescription,
  onFocus,
  onMouseOverCapture,
  onMouseEnter: _tooltipMouseEnter,
  to,
  forceHardNavigation,
  href,
  preload,
  state,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  consumerHandlers: ConsumerHandlers
  expandedDescription?: string
  to?: string
  forceHardNavigation?: boolean
  ref?: Ref<HTMLAnchorElement>
  preload?: ComponentProps<typeof Link>['preload']
  state?: ComponentProps<typeof Link>['state']
}) {
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const anchorProps = {
    ...props,
    'aria-describedby': visualIsExpanded ? expandedDescription : props['aria-describedby'],
    ...(!visualIsExpanded || consumerHandlers.onFocus
      ? {
          onFocus: (event: FocusEvent<HTMLAnchorElement>) => {
            consumerHandlers.onFocus?.(event)
            onFocus?.(event)
          },
        }
      : {}),
    ...(consumerHandlers.onMouseEnter ? {onMouseEnter: consumerHandlers.onMouseEnter} : {}),
    onMouseOverCapture: (event: MouseEvent<HTMLAnchorElement>) => {
      consumerHandlers.onMouseOverCapture?.(event)
      onMouseOverCapture?.(event)
    },
  }
  return to && !forceHardNavigation ? (
    <Link {...anchorProps} to={to} preload={preload} state={state}>
      {children}
    </Link>
  ) : (
    <a {...anchorProps} href={href ?? to}>
      {children}
    </a>
  )
}

export const Item = Object.assign(
  memo(function SidebarItem({
    children,
    href,
    to,
    forceHardNavigation,
    leadingVisual,
    trailingVisual,
    trailingAction,
    trailingActionLayout = 'adjacent',
    actionVisibility = 'always',
    tooltipText,
    inactiveText,
    current,
    'aria-current': ariaCurrent,
    ref,
    className,
    onFocus,
    onMouseEnter,
    onMouseOverCapture,
    ...linkProps
  }: ItemProps) {
    const {visualIsExpanded} = useCollapsibleSidebarContext()
    const grouped = use(GroupedContext)
    const hasOverlayAction = visualIsExpanded && Boolean(trailingAction) && trailingActionLayout === 'overlay'
    const rowRef = useRef<HTMLDivElement>(null)
    const itemRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)
    const [actionDismissed, setActionDismissed] = useState(false)
    useOnEscapePress(event => {
      const row = rowRef.current
      if (
        !hasOverlayAction ||
        actionVisibility !== 'hover' ||
        !trailingVisual ||
        actionDismissed ||
        !row?.matches(':hover, :focus-within') ||
        row.querySelector('[data-sidebar-action] [aria-expanded="true"]') ||
        matchMedia('(hover: none), (pointer: coarse), (any-pointer: coarse)').matches
      )
        return

      event.preventDefault()
      // Never hide the focused action. Restore its destination before dismissing.
      if (row.querySelector('[data-sidebar-action]')?.contains(row.ownerDocument.activeElement)) {
        itemRef.current?.focus()
      }
      setActionDismissed(true)
    })
    const isInactive = inactiveText !== undefined
    const currentState = getCurrentState({current, inactiveText, 'aria-current': ariaCurrent})
    if (!grouped && !leadingVisual) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Sidebar.Item] Items outside a SectionGroup or Accordion need leadingVisual to appear in the rail. This item is only shown when the sidebar is expanded.',
        )
      }
      if (!visualIsExpanded) return null
    }
    const content = (
      <>
        {leadingVisual ? (
          <span className={clsx(styles.LeadingVisual, !grouped && rowStyles.Icon)}>{leadingVisual}</span>
        ) : null}
        <span
          data-sidebar-collapse-content
          className={clsx(rowStyles.Label, !visualIsExpanded && styles.visuallyHidden)}
        >
          {children}
        </span>
        {visualIsExpanded && trailingVisual ? (
          <span data-sidebar-collapse-content className={styles.TrailingVisual}>
            {trailingVisual}
          </span>
        ) : null}
      </>
    )
    const setItemRef = (element: HTMLAnchorElement | HTMLButtonElement | null) => {
      itemRef.current = element
      if (typeof ref === 'function') return ref(element)
      if (ref) ref.current = element
    }
    const anchorProps = {
      ...linkProps,
      ref: setItemRef,
      'aria-current': currentState.ariaCurrent,
      className: clsx(
        rowStyles.Row,
        styles.Link,
        currentState.current && rowStyles.current,
        grouped && !leadingVisual && rowStyles.textLed,
        isInactive && styles.inactive,
        className,
      ),
    }
    const link = isInactive ? (
      <button
        type="button"
        ref={setItemRef}
        className={anchorProps.className}
        aria-label={linkProps['aria-label']}
        aria-labelledby={linkProps['aria-labelledby']}
        aria-disabled="true"
      >
        {hasOverlayAction ? <span className={styles.Content}>{content}</span> : content}
      </button>
    ) : (
      <ItemLink
        {...anchorProps}
        href={href}
        to={to}
        forceHardNavigation={forceHardNavigation}
        consumerHandlers={{onFocus, onMouseEnter, onMouseOverCapture}}
        expandedDescription={linkProps['aria-describedby']}
      >
        {hasOverlayAction ? <span className={styles.Content}>{content}</span> : content}
      </ItemLink>
    )
    const tooltipLabel = tooltipText ?? (typeof children === 'string' ? children : '')
    const tooltipContent = isInactive
      ? [!visualIsExpanded && tooltipLabel, inactiveText].filter(Boolean).join(': ')
      : visualIsExpanded
        ? ''
        : tooltipLabel
    return (
      <div
        ref={rowRef}
        className={clsx(
          styles.Item,
          hasOverlayAction && styles.overlayAction,
          hasOverlayAction && actionVisibility === 'hover' && actionDismissed && styles.actionDismissed,
          actionStyles.actionRow,
          currentState.current && styles.current,
          isInactive && styles.inactive,
        )}
        data-component="Sidebar.Item"
        onMouseLeave={event => {
          if (!event.currentTarget.contains(event.currentTarget.ownerDocument.activeElement)) {
            setActionDismissed(false)
          }
        }}
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget) && !event.currentTarget.matches(':hover')) {
            setActionDismissed(false)
          }
        }}
        onFocusCapture={event => {
          if (!event.currentTarget.contains(event.relatedTarget) || !itemRef.current?.contains(event.target)) {
            setActionDismissed(false)
          }
        }}
      >
        {isInactive || tooltipText || typeof children === 'string' ? (
          <FeatureFlags flags={{primer_react_merged_forwarded_refs: true}}>
            <Tooltip
              type="description"
              text={tooltipContent}
              direction="e"
              delay="medium"
              _privateDisableTooltip={!tooltipContent}
              popover={tooltipContent ? 'auto' : undefined}
            >
              {link}
            </Tooltip>
          </FeatureFlags>
        ) : (
          link
        )}
        {visualIsExpanded && trailingAction ? (
          <SidebarAction hover={actionVisibility === 'hover'} muted={actionVisibility === 'muted'}>
            {trailingAction}
          </SidebarAction>
        ) : null}
      </div>
    )
  }),
  {[sidebarChildKind]: 'item' as const},
)
